import React, { useMemo } from 'react';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';

import type { CharterPromotionPricingInventoryRow } from '~/types/charterPromotionPricingInventory';

import { charterAirportOptions, charterCabinOptions } from '../FriendAndFamily/charterAirports';
import type { CharterCabinClass } from '../charter.types';

export interface PromotionFormValues {
  depAirport: string;
  arrAirport: string;
  cabin: CharterCabinClass;
  depDate: string;
  availableDaysBefore: string;
  promotionAllocation: string;
  adultPrice: string;
  childPrice: string;
  eligibility: 'LUXPLUS_ONLY' | 'ALL_MEMBERS' | 'STANDARD_ONLY';
}

interface Props {
  open: boolean;
  loading?: boolean;
  values: PromotionFormValues;
  title: string;
  submitLabel: string;
  existingRows?: Array<CharterPromotionPricingInventoryRow>;
  isEditMode?: boolean;
  minimumPromotionAllocation?: number;
  onClose: () => void;
  onSubmit: () => void;
  onChange: (field: keyof PromotionFormValues, value: string) => void;
}

const integerFieldPattern = /^\d*$/;

export default function CharterPromotionDialog({
  open,
  loading,
  values,
  title,
  submitLabel,
  existingRows = [],
  isEditMode = false,
  minimumPromotionAllocation = 0,
  onClose,
  onSubmit,
  onChange,
}: Props): React.JSX.Element {
  const hasDuplicateRow = useMemo(() => {
    if (isEditMode || !values.depAirport || !values.arrAirport || !values.depDate) {
      return false;
    }

    return existingRows.some(
      (row) =>
        row.depAirport === values.depAirport &&
        row.arrAirport === values.arrAirport &&
        row.date === values.depDate &&
        row.cabin === values.cabin,
    );
  }, [isEditMode, existingRows, values.depAirport, values.arrAirport, values.cabin, values.depDate]);
  const isPromotionAllocationInvalid =
    isEditMode &&
    values.promotionAllocation.trim() !== '' &&
    Number(values.promotionAllocation) < minimumPromotionAllocation;

  const handleIntegerChange = (field: keyof PromotionFormValues) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;

    if (integerFieldPattern.test(value)) {
      onChange(field, value);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" slotProps={{ paper: { sx: { p: 1.25 } } }}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {hasDuplicateRow && (
            <Alert severity="warning">
              A promotion row with the same departure airport, arrival airport, and departure date already exists.
              Submitting will override the existing row.
            </Alert>
          )}
          <Stack direction="row" spacing={2}>
            <TextField
              label="Departure airport"
              select
              value={values.depAirport}
              onChange={(event) => onChange('depAirport', event.target.value)}
              fullWidth
              required
            >
              {charterAirportOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Arrival airport"
              select
              value={values.arrAirport}
              onChange={(event) => onChange('arrAirport', event.target.value)}
              fullWidth
              required
            >
              {charterAirportOptions.map((code) => (
                <MenuItem key={code} value={code}>
                  {code}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
          <TextField
            label="Departure date"
            type="date"
            value={values.depDate}
            onChange={(event) => onChange('depDate', event.target.value)}
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Cabin"
            select
            value={values.cabin}
            onChange={(event) => onChange('cabin', event.target.value as CharterCabinClass)}
            disabled={isEditMode}
            fullWidth
            required
            helperText={isEditMode ? 'Cabin class cannot be changed after a promotion row is created.' : undefined}
          >
            {charterCabinOptions.map((cabin) => (
              <MenuItem key={cabin.value} value={cabin.value}>
                {cabin.label}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label="Eligibility"
            select
            value={values.eligibility}
            onChange={(event) => onChange('eligibility', event.target.value as PromotionFormValues['eligibility'])}
            fullWidth
          >
            <MenuItem value="ALL_MEMBERS">LuxPlus and standard members</MenuItem>
            <MenuItem value="LUXPLUS_ONLY">LuxPlus members only</MenuItem>
            <MenuItem value="STANDARD_ONLY">Standard members only</MenuItem>
          </TextField>
          <TextField
            label="Available Days Before"
            type="number"
            value={values.availableDaysBefore}
            onChange={handleIntegerChange('availableDaysBefore')}
            fullWidth
            required
            inputProps={{ step: 1, inputMode: 'numeric' }}
          />
          <TextField
            label="Promotion allocation"
            type="number"
            value={values.promotionAllocation}
            onChange={handleIntegerChange('promotionAllocation')}
            fullWidth
            required
            error={isPromotionAllocationInvalid}
            helperText={
              isPromotionAllocationInvalid
                ? `Promotion allocation cannot be less than Total Booked (${minimumPromotionAllocation}).`
                : undefined
            }
            inputProps={{ step: 1, inputMode: 'numeric', min: isEditMode ? minimumPromotionAllocation : 0 }}
          />
          <TextField
            label="Adult Price"
            type="number"
            value={values.adultPrice}
            onChange={(event) => onChange('adultPrice', event.target.value)}
            fullWidth
            required
            error={values.adultPrice !== '' && Number(values.adultPrice) < 0}
            helperText={values.adultPrice !== '' && Number(values.adultPrice) < 0 ? 'Price cannot be less than 0' : ''}
          />
          <TextField
            label="Child Price"
            type="number"
            value={values.childPrice}
            onChange={(event) => onChange('childPrice', event.target.value)}
            fullWidth
            required
            error={values.childPrice !== '' && Number(values.childPrice) < 0}
            helperText={values.childPrice !== '' && Number(values.childPrice) < 0 ? 'Price cannot be less than 0' : ''}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button variant="text" onClick={onClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={onSubmit}
          loading={loading}
          disabled={
            Object.values(values).some((value) => value.trim() === '') ||
            isPromotionAllocationInvalid ||
            Number(values.adultPrice) < 0 ||
            Number(values.childPrice) < 0
          }
        >
          {submitLabel}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
